import { ColGroupDef, ColDef, GridOptions, ValueFormatterParams } from 'ag-grid-community'
import { TradingControlLimitDetail } from 'api'
import {
  withAllContext,
  createGroupedActionRenderer,
  createActionsRenderer,
  groupingRowColDef,
  TypedValueGetterParams,
  gridOptionsBase,
  TypedHeaderValueGetterParams
} from 'view/components/datagrid'
import { TradingControlActionsButton } from './TradingControlActionsButton'
import { Locale, Translation } from 'locale'
import { getNotionalFormatOptions, makeNotionalFilterGetter } from 'view/shared/datagrid'
import { Decimal } from 'utils'

enum TradingControlGridColumnId {
  TRADING_CONTROL_CHECKBOX = 'TRADING_CONTROL_CHECKBOX',
  TRADING_CONTROL_ACTIONS = 'TRADING_CONTROL_ACTIONS',
  TRADING_CONTROL_ORGANISATION = 'TRADING_CONTROL_ORGANISATION',
  TRADING_CONTROL_LIMIT = 'TRADING_CONTROL_LIMIT',
  TRADING_CONTROL_UTILISATION = 'TRADING_CONTROL_UTILISATION',
  TRADING_CONTROL_AVAILABLE = 'TRADING_CONTROL_AVAILABLE'
}

const tradingControlValueFormatter = (params: ValueFormatterParams) => {
  const formatOptions = getNotionalFormatOptions(params.context.userPreference.amountFormat)
  return Locale.formatNumber(params.value, formatOptions)
}

export const TradingControlColumnDefs: (ColGroupDef | ColDef)[] = [
  {
    colId: TradingControlGridColumnId.TRADING_CONTROL_CHECKBOX,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    checkboxSelection: true,
    width: 36,
    maxWidth: 36,
    pinned: 'left',
    lockPinned: true,
    enableRowGroup: false,
    lockPosition: true,
    lockVisible: true,
    filter: false,
    resizable: false,
    sortable: false
  },
  groupingRowColDef,
  {
    colId: TradingControlGridColumnId.TRADING_CONTROL_ACTIONS,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    equals: () => false,
    width: 180,
    minWidth: 180,
    headerComponent: withAllContext(createGroupedActionRenderer(TradingControlActionsButton)),
    cellClass: 'action-cell',
    cellRenderer: withAllContext(createActionsRenderer(TradingControlActionsButton)),
    pinned: 'left',
    lockPinned: true,
    enableRowGroup: false,
    lockVisible: true,
    filter: false,
    sortable: false
  },
  {
    colId: TradingControlGridColumnId.TRADING_CONTROL_ORGANISATION,
    valueGetter: (params: TypedValueGetterParams<TradingControlLimitDetail>) => params.data?.organisation?.legalName,
    headerName: Locale.translate(Translation.TC_ORGANISATION),
    width: 180,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradingControlGridColumnId.TRADING_CONTROL_LIMIT,
    valueGetter: (params: TypedValueGetterParams<TradingControlLimitDetail>) => params.data?.tradingControlLimit.limit,
    headerValueGetter: (params: TypedHeaderValueGetterParams<TradingControlLimitDetail>) =>
      Locale.translate(Translation.TC_LIMIT, { amountFormat: params.context.userPreference.amountFormat }),
    width: 150,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: tradingControlValueFormatter,
    filterValueGetter: makeNotionalFilterGetter(),
    enableRowGroup: false,
    equals: () => false
  },
  {
    colId: TradingControlGridColumnId.TRADING_CONTROL_UTILISATION,
    valueGetter: (params: TypedValueGetterParams<TradingControlLimitDetail>) =>
      params.data && Decimal.abs(params.data.tradingControlLimit.utilisation),
    headerValueGetter: (params: TypedHeaderValueGetterParams<TradingControlLimitDetail>) =>
      Locale.translate(Translation.TC_UTILISATION, { amountFormat: params.context.userPreference.amountFormat }),
    width: 150,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: tradingControlValueFormatter,
    filterValueGetter: makeNotionalFilterGetter(),
    enableRowGroup: false,
    equals: () => false
  },
  {
    colId: TradingControlGridColumnId.TRADING_CONTROL_AVAILABLE,
    valueGetter: (params: TypedValueGetterParams<TradingControlLimitDetail>) => params.data?.tradingControlLimit.displayAvailable,
    headerValueGetter: (params: TypedHeaderValueGetterParams<TradingControlLimitDetail>) =>
      Locale.translate(Translation.TC_AVAILABLE, { amountFormat: params.context.userPreference.amountFormat }),
    width: 150,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: tradingControlValueFormatter,
    filterValueGetter: makeNotionalFilterGetter(),
    enableRowGroup: false,
    equals: () => false
  }
]

export const tradingControlGridOptions: GridOptions = {
  ...gridOptionsBase,
  getRowId: ({ data }) => data.tradingControlLimit.id
}
